import { z } from 'zod';
import { DomainError } from '../errors.js';
import type { ParseResult } from '../voyage/schemas.js';
import type { PdiResult } from './inspection.js';
import { day, optionalParagraph, parsed, requiredParagraph, typed } from './schema.js';
import type { DrydockJobStatus, PdiFindingStatus } from './scope.js';

/**
 * Pre-Docking Faz 4: tersane takibi ve kapanış (ADR 0129 Ek Faz 4; Excel "Software Workflow" 10,
 * 12, "Screen Workflow" 9–11; Form Job "RA / PTW Review" ve "Approval State" Draft / For Review /
 * Approved / In Progress / Verified / Closed). Ortak: "onaylanan işler iş listesine düşer ve
 * tersanede takibi yapılır, biten iş kapatılır. yapılmayanlar pending olarak kalır". Saf modül.
 *
 * - İcra gemide değiştirilemez olaylardır; durum saklanmaz, olaylardan ve ofisin doğrulamasından
 *   türetilir.
 * - Biten iş ofiste Superintendent doğrulamasıyla kapanır; doğrulanmadan kapanmaz.
 * - RA / PTW kendiliğinden onaylanmaz (R8): başlarken yetkilendirilmiş operasyonel doğrulama bağlı
 *   olmalı ya da "gerekmez" gerekçesi yazılır; Felix izin vermez, bağ kurar.
 * - Proje kapanınca tamamlanmamış onaylı iş Pending kalır ve sonraki projeye taşınabilir.
 */

export const DRYDOCK_JOB_EVENTS = [
  'started',
  'progress',
  'completed',
  'not_done',
  'closed',
  'pending',
] as const;
export type DrydockJobEvent = (typeof DRYDOCK_JOB_EVENTS)[number];

/** Başlarken RA / PTW incelemesi (Excel "RA / PTW Review — Required before execution"). */
export const DRYDOCK_SAFETY_REVIEWS = ['linked', 'not_required'] as const;
export type DrydockSafetyReview = (typeof DRYDOCK_SAFETY_REVIEWS)[number];

export const DRYDOCK_VERIFICATION_VERDICTS = ['verified', 'returned'] as const;
export type DrydockVerificationVerdict = (typeof DRYDOCK_VERIFICATION_VERDICTS)[number];

/** Ofis doğrulamasının konusu: işin tamamlanması ya da kritik bulgunun kapanışı. */
export const DRYDOCK_VERIFICATION_SUBJECTS = ['job', 'finding'] as const;
export type DrydockVerificationSubject = (typeof DRYDOCK_VERIFICATION_SUBJECTS)[number];

export const DRYDOCK_EXECUTION_STATES = [
  'not_started',
  'in_progress',
  'completed',
  'verified',
  'closed',
  'pending',
] as const;
export type DrydockExecutionState = (typeof DRYDOCK_EXECUTION_STATES)[number];

/** Denetim örneğinin türü: tersane öncesi ya da After Docking (R5; Software Workflow 10). */
export const PDI_INSPECTION_KINDS = ['pre_docking', 'after_docking'] as const;
export type PdiInspectionKind = (typeof PDI_INSPECTION_KINDS)[number];

/** Gelecek zaman payı (saat farkı ve cihaz saati; denetim sonucuyla aynı). */
const FUTURE_SKEW_MS = 5 * 60 * 1000;
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
const instant = z
  .string(typed('INVALID_DATE'))
  .regex(ISO, 'INVALID_DATE')
  .refine((value) => !Number.isNaN(new Date(value).getTime()), 'INVALID_DATE')
  .transform((value) => new Date(value).toISOString());

// --- İcra olayı ---

export interface DrydockJobEventOptions {
  /** Gemi sunucusu saati (ISO-8601 UTC): olay bundan sonra olamaz. */
  now: string;
}

function jobEventSchema(options: DrydockJobEventOptions) {
  return z
    .strictObject({
      event: z.enum(DRYDOCK_JOB_EVENTS, typed('INVALID_OPTION')),
      /** İlerleme yüzdesi (yalnız `progress`; tamamlanma 100'dür). */
      percent: z
        .number(typed('INVALID_NUMBER'))
        .int('INVALID_NUMBER')
        .min(1, 'INVALID_NUMBER')
        .max(99, 'INVALID_NUMBER')
        .nullish()
        .transform((value) => value ?? null),
      /** Not: tamamlanmada sonuç / test, yapılamadı ve Pending'de gerekçe. */
      note: optionalParagraph(2000),
      safetyReview: z
        .enum(DRYDOCK_SAFETY_REVIEWS, typed('INVALID_OPTION'))
        .nullish()
        .transform((value) => value ?? null),
      /** Olayın zamanı (UTC); gemi saati farkı yazma yolundan (R7). */
      occurredAt: instant,
    })
    .superRefine((value, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });
      if (value.event === 'progress' && value.percent === null) issue('percent', 'REQUIRED');
      if (value.event !== 'progress' && value.percent !== null) issue('percent', 'INVALID_OPTION');
      if (value.event === 'started') {
        if (value.safetyReview === null) issue('safetyReview', 'REQUIRED');
        // "Gerekmez" kararı gerekçesiyle (R8: PTW yalnız RA ve SMS gerektirdiğinde)
        if (value.safetyReview === 'not_required' && !value.note) issue('note', 'REASON_REQUIRED');
      } else if (value.safetyReview !== null) {
        issue('safetyReview', 'INVALID_OPTION');
      }
      if (value.event === 'completed' && !value.note) issue('note', 'NOTES_REQUIRED');
      if ((value.event === 'not_done' || value.event === 'pending') && !value.note) {
        issue('note', 'REASON_REQUIRED');
      }
      if (new Date(value.occurredAt).getTime() > new Date(options.now).getTime() + FUTURE_SKEW_MS) {
        issue('occurredAt', 'FUTURE_DATE');
      }
    });
}
export type DrydockJobEventInput = z.output<ReturnType<typeof jobEventSchema>>;

export function parseDrydockJobEvent(
  input: unknown,
  options: DrydockJobEventOptions,
): ParseResult<DrydockJobEventInput> {
  return parsed(jobEventSchema(options).safeParse(input));
}

// --- Ofis doğrulaması ---

export const drydockVerificationSchema = z
  .strictObject({
    verdict: z.enum(DRYDOCK_VERIFICATION_VERDICTS, typed('INVALID_OPTION')),
    note: optionalParagraph(2000),
    contentHash: z.string(typed('INVALID_HASH')).regex(/^[0-9a-f]{64}$/, 'INVALID_HASH'),
  })
  .superRefine((value, ctx) => {
    // İade (yeniden işçilik) gerekçe ister
    if (value.verdict === 'returned' && !value.note) {
      ctx.addIssue({ code: 'custom', path: ['note'], message: 'REASON_REQUIRED' });
    }
  });
export type DrydockVerificationInput = z.output<typeof drydockVerificationSchema>;

export function parseDrydockVerification(input: unknown): ParseResult<DrydockVerificationInput> {
  return parsed(drydockVerificationSchema.safeParse(input));
}

// --- Dock tarihleri, proje kapanışı, bulgu kapanışı ---

export function parseDrydockDockDate(
  input: unknown,
  options: { today: string },
): ParseResult<{ on: string }> {
  return parsed(
    z
      .strictObject({ on: day })
      .superRefine((value, ctx) => {
        if (value.on > options.today) {
          ctx.addIssue({ code: 'custom', path: ['on'], message: 'FUTURE_DATE' });
        }
      })
      .safeParse(input),
  );
}

export function parseDrydockCloseout(
  input: unknown,
  options: { today: string; dockedOn: string | null },
): ParseResult<{ undockedOn: string; note: string | null }> {
  return parsed(
    z
      .strictObject({ undockedOn: day, note: optionalParagraph(2000) })
      .superRefine((value, ctx) => {
        const issue = (message: string) =>
          ctx.addIssue({ code: 'custom', path: ['undockedOn'], message });
        if (value.undockedOn > options.today) issue('FUTURE_DATE');
        else if (options.dockedOn && value.undockedOn < options.dockedOn) {
          issue('END_BEFORE_START');
        }
      })
      .safeParse(input),
  );
}

export function parsePdiFindingClose(input: unknown): ParseResult<{ note: string }> {
  return parsed(z.strictObject({ note: requiredParagraph(2000) }).safeParse(input));
}

// --- Türetilmiş icra durumu ---

export interface DrydockJobEventRef {
  id: string;
  event: DrydockJobEvent;
  percent: number | null;
  provisional: boolean;
  contentHash: string;
}

export interface DrydockVerificationRef {
  eventId: string;
  verdict: DrydockVerificationVerdict;
  contentHash: string;
}

export interface DrydockExecutionView {
  state: DrydockExecutionState;
  /** Son ilerleme yüzdesi; tamamlanmış, doğrulanmış ve kapalı işte 100. */
  percent: number | null;
  /** Ofis onayı gelmeden (ek iş) başlatıldı. */
  provisional: boolean;
  /** Ofis doğrulamasının verileceği son tamamlanma (durum `completed` / `verified`). */
  completionId: string | null;
  /** Son tamamlanmaya verilen ofis kararı. */
  verification: DrydockVerificationVerdict | null;
  lastEventId: string | null;
}

/**
 * İcra durumu (olay sırasıyla): started / progress → In Progress; completed → Completed, ofis
 * doğrulayınca Verified, iade edince In Progress (yeniden işçilik); closed → Closed; not_done ve
 * pending → Pending.
 */
export function drydockExecution(input: {
  events: readonly DrydockJobEventRef[];
  verifications: readonly DrydockVerificationRef[];
}): DrydockExecutionView {
  let state: DrydockExecutionState = 'not_started';
  let percent: number | null = null;
  let completionId: string | null = null;
  let verification: DrydockVerificationVerdict | null = null;
  for (const event of input.events) {
    switch (event.event) {
      case 'started':
        state = 'in_progress';
        percent = null;
        completionId = null;
        verification = null;
        break;
      case 'progress':
        state = 'in_progress';
        percent = event.percent;
        break;
      case 'completed': {
        const verdict = input.verifications.find((v) => v.eventId === event.id)?.verdict ?? null;
        completionId = event.id;
        verification = verdict;
        if (verdict === 'returned') {
          state = 'in_progress';
          percent = null;
        } else {
          state = verdict === 'verified' ? 'verified' : 'completed';
          percent = 100;
        }
        break;
      }
      case 'closed':
        state = 'closed';
        percent = 100;
        break;
      case 'not_done':
      case 'pending':
        state = 'pending';
        completionId = null;
        break;
    }
  }
  return {
    state,
    percent,
    provisional: input.events.some((e) => e.provisional),
    completionId: state === 'completed' || state === 'verified' ? completionId : null,
    verification,
    lastEventId: input.events.at(-1)?.id ?? null,
  };
}

const ALLOWED: Readonly<Record<DrydockExecutionState, readonly DrydockJobEvent[]>> = {
  not_started: ['started', 'not_done'],
  in_progress: ['progress', 'completed', 'not_done'],
  // Tamamlanmış iş ofis doğrulamasını bekler (iade yeniden işçiliğe döndürür)
  completed: [],
  verified: ['closed'],
  closed: [],
  pending: ['started'],
};

/**
 * Olayın kuralı (Excel "Approval gate"): ilerleme yalnız proje tersanedeyken; başlama, ilerleme ve
 * tamamlanma onaylı (hash tutan) işte ya da ofiste bekleyen ek işte (provisional; ADR 0129 §6
 * önerisi); kapanış yalnız doğrulanmış onaylı işte; yapılamadı her zaman yazılır; Pending yalnız
 * proje kapanışında (kapalı iş dışında). Başlarken "linked" seçimi bağlı RA doğrulaması ister.
 */
export function drydockJobTransition(input: {
  execution: DrydockExecutionView;
  approval: DrydockJobStatus;
  additional: boolean;
  event: DrydockJobEvent;
  projectState: string;
  raLinked: boolean;
  safetyReview?: DrydockSafetyReview | null;
}): { provisional: boolean } {
  const { state } = input.execution;
  if (input.event === 'pending') {
    if (state === 'closed') throw new DomainError('DRYDOCK_JOB_TRANSITION');
    return { provisional: false };
  }
  if (input.projectState !== 'in_yard') throw new DomainError('DRYDOCK_PROJECT_NOT_IN_YARD');
  if (input.event === 'closed' && state !== 'verified' && state !== 'closed') {
    throw new DomainError('DRYDOCK_JOB_NOT_VERIFIED');
  }
  if (!ALLOWED[state].includes(input.event)) {
    throw new DomainError('DRYDOCK_JOB_TRANSITION');
  }
  let provisional = false;
  if (input.event !== 'not_done') {
    if (input.approval === 'approved') provisional = false;
    else if (input.additional && input.approval === 'for_review' && input.event !== 'closed') {
      provisional = true;
    } else throw new DomainError('DRYDOCK_JOB_NOT_APPROVED');
  }
  if (input.event === 'started' && input.safetyReview === 'linked' && !input.raLinked) {
    throw new DomainError('DRYDOCK_RA_REVIEW_REQUIRED');
  }
  return { provisional };
}

/** Ofis doğrulaması yalnız onaylı işin son tamamlanmasına (Screen 10 "Execution & Verification"). */
export function assertDrydockVerifiable(input: {
  execution: DrydockExecutionView;
  approval: DrydockJobStatus;
  eventId: string;
}): void {
  if (input.approval !== 'approved') {
    throw new DomainError('DRYDOCK_JOB_NOT_APPROVED');
  }
  if (input.execution.state !== 'completed' || input.execution.completionId !== input.eventId) {
    throw new DomainError('STALE_CONTENT');
  }
}

export interface DrydockCloseoutJob {
  id: string;
  cancelled: boolean;
  approval: DrydockJobStatus;
  state: DrydockExecutionState;
}

/**
 * Proje kapanış planı (ortak: "biten iş kapatılır. yapılmayanlar pending olarak kalır"):
 * doğrulanmış onaylı iş Closed; onaylı ya da tersanede başlamış, kapanmamış iş Pending; zaten
 * Pending olan yeniden yazılmaz; onaylanmamış ve başlamamış taslak dokunulmaz.
 */
export function drydockCloseoutPlan(jobs: readonly DrydockCloseoutJob[]): {
  close: string[];
  pending: string[];
  alreadyPending: string[];
  untouched: string[];
} {
  const plan = {
    close: [] as string[],
    pending: [] as string[],
    alreadyPending: [] as string[],
    untouched: [] as string[],
  };
  for (const job of jobs) {
    if (job.cancelled || job.state === 'closed') continue;
    if (job.state === 'verified' && job.approval === 'approved') plan.close.push(job.id);
    else if (job.state === 'pending') plan.alreadyPending.push(job.id);
    else if (job.approval === 'approved' || job.state !== 'not_started') plan.pending.push(job.id);
    else plan.untouched.push(job.id);
  }
  return plan;
}

// --- Bulgu kapanışı ---

/** Bağlı eylem isteyen kararlar (Monitor / No action notla kapanır). */
const ACTION_DISPOSITIONS = ['repair', 'dry_dock', 'survey'];

/**
 * Bulgu kapanışı (Excel Screen 11 "Close Finding — Confirm linked actions complete or approved
 * deferral"; R15, R16): bulgu incelenmiş; bağlı işler Closed ya da Pending (onaylı erteleme);
 * reddedilen ve iptal edilen işler sayılmaz; bağlı arızalar arıza modülünde kapanmış; Repair / Dry
 * Dock / Survey kararında en az bir bağlı eylem.
 */
export function assertPdiFindingClosable(input: {
  status: PdiFindingStatus;
  disposition: string;
  jobs: readonly {
    jobNo: string;
    cancelled: boolean;
    approval: DrydockJobStatus;
    state: DrydockExecutionState;
  }[];
  defects: readonly { defectNo: string; closed: boolean }[];
  surveys: number;
}): void {
  if (input.status === 'cancelled') throw new DomainError('DRYDOCK_RECORD_CANCELLED');
  if (input.status === 'closed' || input.status === 'closing') {
    throw new DomainError('PDI_FINDING_CLOSED');
  }
  if (input.status === 'draft') throw new DomainError('PDI_FINDING_NOT_REVIEWED');
  const jobs = input.jobs.filter((j) => !j.cancelled && j.approval !== 'rejected');
  const open = jobs.find((j) => j.state !== 'closed' && j.state !== 'pending');
  if (open) throw new DomainError('PDI_FINDING_ACTIONS_OPEN');
  const defect = input.defects.find((d) => !d.closed);
  if (defect) throw new DomainError('PDI_FINDING_DEFECT_OPEN');
  if (
    ACTION_DISPOSITIONS.includes(input.disposition) &&
    jobs.length + input.defects.length + input.surveys === 0
  ) {
    throw new DomainError('PDI_FINDING_NO_ACTION');
  }
}

// --- After Docking kuyruğu ---

/**
 * After Docking kuyruğu (R5; Software Workflow 10 "Carry forward inaccessible checkpoints to
 * post-docking inspection queue"): denetimler sırasıyla verilir (her birinde noktanın son
 * revizyonu); son sonucu Deferred olan noktalar kuyruktadır.
 */
export function pdiAfterDockingQueue(
  rounds: readonly (readonly { checkpointCode: string; result: PdiResult | string }[])[],
): string[] {
  const last = new Map<string, string>();
  for (const round of rounds) {
    for (const result of round) last.set(result.checkpointCode, result.result);
  }
  return [...last.entries()]
    .filter(([, result]) => result === 'deferred')
    .map(([code]) => code)
    .sort();
}
